import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
  PartnerProductProposalMetricsTableVariables,
  PartnerProposalMetricsTableDataResponse,
} from '../../../api/types';
import { PARTNER_PROFILE_PROPOSAL_METRICS } from 'modules/aftermarketPartner/api';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import makeStyles from '@mui/styles/makeStyles';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import calendar from 'assets/img/icons/calendar.png';
import { Box, Button, ButtonGroup, FormControlLabel, Grid2, Switch } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { ReportsTable } from '../../tables/reportsTable/ReportsTable';
import { ColumnSelector } from '../../tables/reportsTable/columnSelector';
import { createTableColumns } from '../../tables/reportsTable/tableConfig';
import { Column } from 'react-table';

const useStyles = makeStyles({
  datePicker: {
    fontSize: '14px',
    width: '120px',
    textAlign: 'right',
    padding: '5px',
    border: '1px solid #CCC',
    borderRadius: '5px',
    backgroundImage: `url(${calendar})`,
    backgroundPosition: 'left',
    backgroundRepeat: 'no-repeat',
  },
  calendarContainer: {
    marginBottom: '20px',
    borderBottom: '1px solid #ccc1c1',
    padding: '10px',
  },
  salesRepContainer: {
    marginTop: '30px',
  },
  noResults: {
    fontSize: '1rem',
    marginTop: '15px',
  },
  separator: {
    borderTop: '1px solid #a2adb3',
  },
  categoryHeader: {
    fontVariant: 'small-caps',
    fontSize: '15px',
    color: 'black',
  },
  indentItem: {
    paddingLeft: '2rem',
    fontSize: '14px',
  },
  left: {
    textAlign: 'left',
  },
  header1stElement: {
    textAlign: 'center',
    textShadow: '1px 1px 4px #CCC',
    fontSize: '1rem !important',
  },
  headerCentered: {
    textAlign: 'center',
    textShadow: '1px 1px 4px #CCC',
    fontWeight: 'bold',
  },
  cellsCentered: {
    textAlign: 'center',
  },
  cellsCenteredFixedWidth: {
    textAlign: 'center',
    minWidth: '120px',
  },
});

export const ReportsPage: React.FC = () => {
  const account = useSelector((state: any) => state.account);
  const classes = useStyles();
  const [yesterday, setYesterday] = useState<any>(new Date().setDate(new Date().getDate() - 1));
  const [today, setToday] = useState<Date>(new Date());
  const [partnerView, setPartnerView] = useState<boolean>(true);

  const [startDate, setStartDate] = useState<any>(new Date().setMonth(new Date().getMonth() - 3));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [columns, setColumns] = useState<Column[]>([]);

  const quickDateActions = (key: any) => {
    const handleSetCalendar = (e: any) => {
      e.preventDefault();
      const range = e.currentTarget.id;
      let startDate, endDate;

      switch (range) {
        case 'today':
          startDate = new Date();
          endDate = new Date();
          break;
        case 'yesterday':
          startDate = addDays(new Date(), -1);
          endDate = addDays(new Date(), -1);
          break;
        case 'this_week':
          startDate = startOfWeek(new Date());
          endDate = endOfWeek(new Date());
          break;
        case 'last_week':
          startDate = startOfWeek(addDays(new Date(), -7));
          endDate = endOfWeek(addDays(new Date(), -7));
          break;
        case 'this_month':
          startDate = startOfMonth(new Date());
          endDate = endOfMonth(new Date());
          break;
        case 'last_month':
          startDate = startOfMonth(addDays(new Date(), -1 * new Date().getDate()));
          endDate = endOfMonth(addDays(new Date(), -1 * new Date().getDate()));
          break;
        case 'this_quarter':
          startDate = startOfQuarter(new Date());
          endDate = endOfQuarter(new Date());
          break;
        case 'last_quarter':
          startDate = startOfQuarter(addMonths(new Date(), -3));
          endDate = endOfQuarter(addMonths(new Date(), -3));
          break;
        case 'this_year':
          startDate = startOfYear(new Date());
          endDate = endOfYear(new Date());
          break;
        case 'last_year':
          startDate = startOfYear(addDays(new Date(), -365));
          endDate = endOfYear(addDays(new Date(), -365));
          break;
      }

      setStartDate(startDate);
      setEndDate(endDate as Date);
    };
    const optionList = [
      { lbl: 'Today', id: 'today' },
      { lbl: 'Yesterday', id: 'yesterday' },
      { lbl: 'This Week', id: 'this_week' },
      { lbl: 'Last Week', id: 'last_week' },
      { lbl: 'This Month', id: 'this_month' },
      { lbl: 'Last Month', id: 'last_month' },
      { lbl: 'This Quarter', id: 'this_quarter' },
      { lbl: 'Last Quarter', id: 'last_quarter' },
      { lbl: 'This Year', id: 'this_year' },
      { lbl: 'Last Year', id: 'last_year' },
    ];
    return (
      <Box
        key={key}
        sx={{
          height: '100%',
          display: 'flex',
          '& > *': {
            m: 1,
          },
        }}
      >
        <ButtonGroup orientation="vertical" variant="contained" style={{ position: 'absolute', minWidth: '25vh' }}>
          {optionList.map(opt => (
            <Button size="small" id={opt.id} key={opt.id} onClick={handleSetCalendar}>
              {opt.lbl}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
    );
  };
  const { data, loading } = useQuery<
    PartnerProposalMetricsTableDataResponse,
    PartnerProductProposalMetricsTableVariables
  >(PARTNER_PROFILE_PROPOSAL_METRICS, {
    skip: !account.id,
    variables: {
      input: {
        from: format(startDate, 'yyyy-MM-dd'),
        to: format(addDays(endDate, 1), 'yyyy-MM-dd'),
        aggregateByPartner: partnerView,
      },
    },
  });
  const handleChangeByProductView = () => {
    setPartnerView(!partnerView);
  };
  useEffect(() => {
    setColumns(createTableColumns({ partnerView }));
  }, [partnerView]);

  // helper to measure longest word width (in px)
  const measureLongestWordPx = (strings: string[]) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    ctx.font = getComputedStyle(document.body).font || '14px Roboto, Arial';
    // const words = strings.flatMap(s => String(s).split(/\s+/).filter(Boolean));
    return Math.max(0, ...strings.map(s => ctx.measureText(s).width));
  };

  useEffect(() => {
    const targetAccessor = partnerView ? 'productName' : 'accountName';

    const idx = columns.findIndex((c: any) => c?.accessor === targetAccessor || c?.id === targetAccessor);
    if (idx === -1) return; // column not present/visible yet

    const header = String((columns[idx] as any).Header ?? '');
    const rows = data?.partnerProfileProductProposalMetrics ?? [];
    const values = [header, ...rows.map((r: any) => String(r?.[targetAccessor] ?? ''))];

    const padding = 28;
    const width = Math.ceil(measureLongestWordPx(values) + padding);

    setColumns(prev => {
      if (!prev.length) return prev;
      const cur = prev[idx] as any;
      if ((cur?.width ?? 0) === width && (cur?.minWidth ?? 0) === width) return prev;
      const next = [...prev];
      next[idx] = { ...cur, width, minWidth: width };
      return next;
    });

    // deps: re-run when the view flips, the data changes, or columns are regenerated
  }, [partnerView, data?.partnerProfileProductProposalMetrics, columns.length]);

  return (
    <CardContainer title={partnerView ? 'Product View' : 'Dealer View'}>
      <Grid2 container>
        <FormControlLabel
          control={<Switch defaultChecked checked={partnerView} onChange={handleChangeByProductView} />}
          label={
            partnerView ? (
              <span>
                By Product <span style={{ fontSize: 13 }}>(Click to view by Dealer)</span>
              </span>
            ) : (
              <span>
                By Dealer <span style={{ fontSize: 13 }}>(Click to view by Product)</span>
              </span>
            )
          }
        />
      </Grid2>

      <Grid2 container rowSpacing={2} flexDirection={'row'}>
        <Grid2 container spacing={2}>
          <Grid2 columns={3}>
            <p>From: </p>
            <DatePicker
              selected={startDate}
              className={classes.datePicker}
              onChange={(date: Date) => setStartDate(date)}
              selectsStart
              dateFormat="yyyy-MM-dd"
              startDate={startDate}
              endDate={endDate}
              maxDate={yesterday}
            >
              {quickDateActions('startDateCal')}
            </DatePicker>
          </Grid2>
          <Grid2 columns={3}>
            <p>To: </p>
            <DatePicker
              className={classes.datePicker}
              selected={endDate}
              dateFormat="yyyy-MM-dd"
              onChange={(date: Date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={today}
            />
          </Grid2>
          <Grid2 columns={6} flex={1} display="flex" justifyContent="flex-end">
            <ColumnSelector columns={columns} onChange={setColumns} />
          </Grid2>
        </Grid2>

        <Grid2>
          <ReportsTable
            columns={columns}
            partnerView={partnerView}
            metricsData={data?.partnerProfileProductProposalMetrics || []}
            loading={loading}
          />
        </Grid2>
      </Grid2>
    </CardContainer>
  );
};
